import { useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Card, Flex, Text, toast } from "@jsq/ds/core";
import { CancelButton, Form, SubmitButton } from "@jsq/ds/forms";
import { Column } from "@jsq/ds/layouts";
import { Sizes } from "@jsq/ds/styles";
import { AddressType } from "~/graphql/mainEnums";
import { IndividualAccountTypeEnum } from "~/graphql/supergraphEnums";
import {
  useCreateIndividualProfileMutation,
  useLinkBeneficialOwnerMutation,
  useLinkControllingPersonMutation,
  useUpdatePicrPositionDetailsMutation,
} from "~/graphql/supergraphHooks";
import { type ComplianceIndividualProfileInput } from "~/graphql/supergraphTypes";
import { useCurrentCaseContext } from "~/pages/Compliance/context/CurrentCaseContext";
import { toISOFormat } from "~/utils/datetime";
import AddressFields from "../../../common/AddressFields";
import { type RoleState } from "../../../common/AutoProfileCreationCard";
import IndividualFormFields from "../../../common/IndividualFields";
import { buildRelationshipTypes } from "../../../common/ProfileActions/utils";
import ProfileIndividualFormFields from "../../../common/ProfileIndividualFormFields";
import { BackButtonWithinForm } from "../BackButtonWithinForm";
import { linkOwnerControllerRoles } from "../utils/linkOwnerControllerRoles";
import { OWNERSHIP_CAP_EXCEEDED_MESSAGE, wouldExceedOwnershipCap } from "../utils/ownershipCap";
import { useExistingOwnershipTotal } from "../utils/useExistingOwnershipTotal";
import { FIELD_MAPPING, INITIAL_VALUES } from "./constants";
import { buildOnboardingIndividualFields } from "./onboardingProfileFields";
import { getSchema } from "./schema";
import { createIndividualForm } from "./styles_vanilla.css";
import { FormValues } from "./types";

interface Props {
  caseId: number;
  parentProfileId: number | undefined;
  positionId?: string | null;
  inAiExtractionMode?: boolean;
  isBeneficiaryProfile?: boolean;
  isChildOfJointAccount?: boolean;
  isOnboardingCase?: boolean;
  saveButtonLabel?: string;
  backNavigationLabel?: string;
  navigateOnCompletion: (profileId: number) => void;
  navigateOnCancel: () => void;
  extractedData?: Record<string, string> | null;
  initialRoleValues?: RoleState;
}

const CreateIndividualForm = ({
  caseId,
  parentProfileId,
  positionId,
  inAiExtractionMode = false,
  isBeneficiaryProfile = false,
  isChildOfJointAccount = false,
  isOnboardingCase = false,
  saveButtonLabel = "Save and exit",
  backNavigationLabel,
  navigateOnCompletion,
  navigateOnCancel,
  extractedData,
  initialRoleValues,
}: Props) => {
  const history = useHistory();
  const location = useLocation();
  const { aiExtractionSupported, arenaAccessId } = useCurrentCaseContext();
  const existingOwnershipTotal = useExistingOwnershipTotal(caseId, parentProfileId);
  const [createIndividualProfile] = useCreateIndividualProfileMutation({
    fetchPolicy: "network-only",
    refetchQueries: ["GetCaseInvestingProfileRiskRating", "GetPositions"],
    onError: (e) => toast.error(e.message),
  });

  const [linkBeneficialOwner] = useLinkBeneficialOwnerMutation({
    fetchPolicy: "network-only",
    onError: (e) => toast.error(e.message),
  });

  const [linkControllingPerson] = useLinkControllingPersonMutation({
    fetchPolicy: "network-only",
    onError: (e) => toast.error(e.message),
  });

  const [updatePicrPositionDetails] = useUpdatePicrPositionDetailsMutation({
    refetchQueries: ["GetPicrPositionDetails"],
    awaitRefetchQueries: true,
    fetchPolicy: "network-only",
    onError: (e) => {
      toast.error(e.message);
      throw e;
    },
  });

  async function addNewIndividual(
    data: ComplianceIndividualProfileInput,
    roleValues: Pick<FormValues, "isOwner" | "isController" | "ownershipPercentage" | "title">,
  ) {
    if (
      roleValues.isOwner &&
      wouldExceedOwnershipCap(existingOwnershipTotal, roleValues.ownershipPercentage)
    ) {
      toast.error(OWNERSHIP_CAP_EXCEEDED_MESSAGE);
      return;
    }
    try {
      const newProfileResponse = await createIndividualProfile({
        variables: {
          arenaAccessId: arenaAccessId ?? 0,
          data: data,
          parentProfileId: parentProfileId,
        },
      });
      const profileId = newProfileResponse?.data?.createIndividualProfile?.id;
      if (newProfileResponse?.errors || !profileId) {
        toast.error("Failed to create individual profile");
        return;
      }

      if (isOnboardingCase && parentProfileId) {
        await linkOwnerControllerRoles({
          parentProfileId,
          childProfileId: profileId,
          isOwner: roleValues.isOwner,
          isController: roleValues.isController,
          ownershipPercentage: roleValues.ownershipPercentage,
          title: roleValues.title,
          linkBeneficialOwner,
          linkControllingPerson,
        });
      }

      if (positionId) {
        await updatePicrPositionDetails({
          variables: {
            data: {
              caseId: caseId,
              positionId: positionId,
              createdProfileId: profileId,
            },
          },
        });
      }

      toast.success("New profile created successfully");
      navigateOnCompletion(profileId);
    } catch {
      // errors already toasted via onError
    }
  }

  const handleSubmit = async (values: Partial<FormValues>) => {
    const profileValues = {
      taxId: isOnboardingCase ? values.identificationNumber : values.taxId,
      taxIdType: isOnboardingCase ? values.identificationType : values.taxIdType,
      caseId: caseId,
      addresses: [
        {
          addressType: AddressType.Unknown,
          street: values.streetLine1,
          street2: values.streetLine2,
          city: values.city,
          state: values.stateOrProvince,
          postalCode: values.zipCode,
          country: values.country || "US",
        },
      ],
    };
    const trimmedDateOfBirth = values.dateOfBirth ? toISOFormat(values.dateOfBirth) : undefined;
    const roleValues = isChildOfJointAccount ? { isOwner: true, isController: false } : values;
    const roles =
      isBeneficiaryProfile || isOnboardingCase ? undefined : buildRelationshipTypes(roleValues);
    const individualValues = {
      accountType: isChildOfJointAccount
        ? IndividualAccountTypeEnum.Individual
        : values.accountType,
      firstName: values.firstName,
      middleName: values.middleName,
      lastName: values.lastName,
      dateOfBirth: trimmedDateOfBirth,
      taxIdType: isOnboardingCase ? values.identificationType : values.taxIdType,
      ownershipPercentage: values.ownershipPercentage,
      relationshipType: roles,
      title: isOnboardingCase ? undefined : values.title,
    };

    const onboardingFields = isOnboardingCase
      ? buildOnboardingIndividualFields({
          placeOfBirthCountryId: values.placeOfBirthCountryId,
          nationalityCountryId: values.nationalityCountryId,
          sourceOfWealth: values.sourceOfWealth,
        })
      : {};

    const data: ComplianceIndividualProfileInput = {
      ...profileValues,
      ...individualValues,
      ...onboardingFields,
      firstName: values.firstName ?? "",
      lastName: values.lastName ?? "",
    };
    await addNewIndividual(data, {
      isOwner: Boolean(roleValues.isOwner),
      isController: Boolean(roleValues.isController),
      ownershipPercentage: roleValues.ownershipPercentage ?? 0,
      title: values.title ?? "",
    });
  };

  const closeForm = () => {
    history.push({
      pathname: `${location.pathname.split("/individual")[0]}`,
      search: location.search,
    });
  };

  const includeRoleValidation = !(positionId || isBeneficiaryProfile);
  const isAIPrefillFlow = Boolean(extractedData);

  const mergedInitialValues = useMemo(() => {
    const merged: FormValues = { ...INITIAL_VALUES };

    if (initialRoleValues) {
      merged.isOwner = initialRoleValues.isOwner;
      merged.isController = initialRoleValues.isController;
      merged.ownershipPercentage = initialRoleValues.ownershipPercentage;
    }

    (Object.keys(INITIAL_VALUES) as Array<keyof FormValues>).forEach((fieldKey) => {
      const extractedKey = FIELD_MAPPING[fieldKey];
      const extractedValue = extractedKey && extractedData?.[extractedKey];
      if (extractedValue !== undefined && extractedValue !== null && extractedValue !== "") {
        (merged[fieldKey] as typeof extractedValue) = extractedValue;
      }
    });

    return merged;
  }, [extractedData, initialRoleValues]);

  return (
    <Form
      initialValues={mergedInitialValues}
      onSubmit={handleSubmit}
      validationSchema={getSchema(
        includeRoleValidation,
        isChildOfJointAccount,
        isOnboardingCase,
      )}
      submitOnlyChangedValues={!isAIPrefillFlow}
    >
      <Column size="lg">
        <Card className={createIndividualForm}>
          {(inAiExtractionMode || backNavigationLabel) && (
            <BackButtonWithinForm
              onClick={isOnboardingCase ? navigateOnCancel : closeForm}
              displayText={backNavigationLabel || "Select individual or entity"}
            />
          )}
          <Text marginBottom="md" variant="h3">
            New individual
          </Text>
          {!(positionId || isBeneficiaryProfile) && (
            <ProfileIndividualFormFields
              isChildOfJointAccount={isChildOfJointAccount}
              isOnboardingCase={isOnboardingCase}
            />
          )}
          <IndividualFormFields
            includeTitle
            inAiExtractionMode={inAiExtractionMode}
            aiExtractionSupported={aiExtractionSupported}
            isChildOfJointAccount={isChildOfJointAccount}
            showOnboardingFields={isOnboardingCase}
          />
          <AddressFields
            aiExtractionSupported={aiExtractionSupported}
            variant={isOnboardingCase ? "onboarding" : "default"}
          />
        </Card>
        <Flex justifyContent="flex-end" gap={Sizes.MD}>
          <CancelButton onClick={navigateOnCancel}>Back</CancelButton>
          <SubmitButton>{saveButtonLabel}</SubmitButton>
        </Flex>
      </Column>
    </Form>
  );
};

export default CreateIndividualForm;
